// SheetPages - a published view's tracking sheets (guide 19.1 and 19.7).
//
// Each page is a SheetPage row: viewports that REFERENCE the view (never a
// copy of its geometry) plus blocks - the schedule, the status legend and
// counts, a title block. The canvas colours each run by its latest field
// status, and a click, a box or a lasso picks runs; the pick is the same
// one the tracking panel and its footer show, so a status set from either
// place lands on the same rows.
//
// SVG is enough at this size (RT06's register is ~250 runs, guide 19.7);
// a page past a few thousand elements belongs on the model tracking page.
// Still, a drag never repaints the drawing: runs are memoised (a status
// change repaints only its run), picks go through a point index, and pointer
// moves are folded into one update a frame.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageEditor, { blankPage } from "./PageEditor";
import { Segmented, Tooltip, message } from "antd";
import { url, tokenRef } from "api";
import { downloadAuthed } from "../printExport";
import { BELOW, ROOF, fmtFt, levelName, runDash, runWidth, viewsApi } from "./viewModel";
import { bounds, makeStackProjector, pointsAttr, sortedLevels, orthoPlan } from "../glanceModel";

// Not started is a dark slate on the sheet: the plates are pale, and a pale run on them disappears.
export const FIELD_COLOR = { projected: "#475569", procured: "#1F6FD1", being_installed: "#F2B705", installed: "#2E7D32" };
export const FIELD_LABEL = { projected: "Not started", procured: "Procured", being_installed: "Being installed", installed: "Installed" };
// A finger needs a bigger target than a mouse (field use, guide 19.7).
const COARSE = (() => { try { return !!(window.matchMedia && window.matchMedia("(pointer: coarse)").matches); } catch (e) { return false; } })();
const SIZES = { "ARCH D": [36, 24], "ARCH E": [48, 36], "ANSI D": [34, 22], "ARCH C": [24, 18] };

// The rows a viewport shows: its level (either end of a riser), systems,
// zones and crop.
export function viewportRows(rows, vp) {
  const crop = Array.isArray(vp.crop) && vp.crop.length === 4 ? vp.crop : null;
  return (rows || []).filter((r) => {
    if (!r.geom || !r.geom.length || r.level == null) return false;
    if (vp.level && r.level !== vp.level && r.level2 !== vp.level) return false;
    if (Array.isArray(vp.systems) && vp.systems.length && !vp.systems.includes(r.system)) return false;
    if (Array.isArray(vp.zones) && vp.zones.length && !vp.zones.includes(r.zone)) return false;
    if (crop && !r.geom.some(([x, y]) => x >= crop[0] && x <= crop[0] + crop[2] && y >= crop[1] && y <= crop[1] + crop[3])) return false;
    return true;
  });
}

// Point in polygon (ray casting), for the lasso.
export function inside(pt, poly) {
  let hit = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i, i += 1) {
    const [xi, yi] = poly[i]; const [xj, yj] = poly[j];
    if ((yi > pt[1]) !== (yj > pt[1]) && pt[0] < ((xj - xi) * (pt[1] - yi)) / (yj - yi) + xi) hit = !hit;
  }
  return hit;
}

// Guide 19.7 hit testing: an index of the projected points, so a box or a
// lasso looks only at what lies under it. A uniform grid does the R-tree's
// job for points in a 1280-wide viewport.
export function pointIndex(projected, cell = 64) {
  const cells = new Map();
  projected.forEach(({ pts }, n) => pts.forEach(([x, y]) => {
    const k = `${Math.floor(x / cell)}|${Math.floor(y / cell)}`;
    const c = cells.get(k) || cells.set(k, []).get(k);
    if (c[c.length - 1] !== n) c.push(n);
  }));
  return { cell, cells };
}

// Which runs a box or a lasso takes: any drawn point of the run inside it.
export function pickByShape(projected, shape, index = null) {
  const out = [];
  let candidates = projected;
  if (index) {
    const xs = shape.kind === "box" ? [shape.x0, shape.x1] : shape.poly.map((p) => p[0]);
    const ys = shape.kind === "box" ? [shape.y0, shape.y1] : shape.poly.map((p) => p[1]);
    const c = index.cell; const seen = new Set();
    for (let cx = Math.floor(Math.min(...xs) / c); cx <= Math.floor(Math.max(...xs) / c); cx += 1) {
      for (let cy = Math.floor(Math.min(...ys) / c); cy <= Math.floor(Math.max(...ys) / c); cy += 1) (index.cells.get(`${cx}|${cy}`) || []).forEach((n) => seen.add(n));
    }
    candidates = [...seen].sort((a, b) => a - b).map((n) => projected[n]);
  }
  candidates.forEach(({ id, pts }) => {
    const hit = shape.kind === "box"
      ? pts.some(([x, y]) => x >= shape.x0 && x <= shape.x1 && y >= shape.y0 && y <= shape.y1)
      : shape.poly.length >= 3 && pts.some((p) => inside(p, shape.poly));
    if (hit) out.push(id);
  });
  return out;
}

// A viewport's projector: plan is top-down to scale, iso is the glance's
// oblique stack (guide 23), one plate per level shown.
export function makeProjection(manifest, vp, rows, width, allRows = rows) {
  const levels = sortedLevels(manifest);
  const first = levels[0]; const last = levels[levels.length - 1];
  // Below the slab and the roof have no floor of their own: they are drawn on
  // the lowest and the top level's outline, dashed, and labelled.
  const noBasement = !levels.some((l) => (l.order || 0) < 1 || /^B\d/i.test(l.id));
  const plateOf = (id) => {
    if (id === BELOW) return { id, lvl: first, dashed: true, label: "Below slab", note: noBasement && first ? `No basement: under the ${levelName(manifest, first.id)} slab` : "" };
    if (id === ROOF) {
      // What is on the roof but has no position (the rooftop DAS conduit) is said, not dropped.
      const unplaced = (allRows || []).filter((r) => (r.level === ROOF || r.level2 === ROOF) && !(r.geom && r.geom.length)).reduce((n, r) => n + (r.qty || 1), 0);
      return { id, lvl: last, dashed: true, roof: true, label: levelName(manifest, ROOF) || "Roof", note: unplaced ? `${unplaced} on the roof not located` : "" };
    }
    const l = levels.find((x) => x.id === id);
    return l ? { id, lvl: l, dashed: false, label: levelName(manifest, id), note: "" } : null;
  };
  const shown = vp.level ? [plateOf(vp.level)].filter(Boolean) : levels.map((l) => plateOf(l.id));
  const crop = Array.isArray(vp.crop) && vp.crop.length === 4 ? vp.crop : null;
  const shownLevels = shown.map((p) => p.lvl).filter(Boolean);
  const b = crop ? { xmin: crop[0], ymin: crop[1], w: crop[2], h: crop[3] } : bounds(shownLevels.length ? shownLevels : levels);
  if (vp.projection === "plan") {
    const { P, height } = orthoPlan(b, { scale: (width - 60) / b.w, left: 30, bottom: 30, top: 30 });
    return { width, height, plates: shown.map((p) => p.id), P: (x, y) => P(x, y), outline: shown };
  }
  // Plates for below the slab and the roof whenever the VIEW has rows there,
  // located or not (a rooftop DAS with no position still earns its plate).
  const onPlate = (id) => (allRows || []).some((r) => r.level === id || r.level2 === id);
  const hasBelow = !vp.level && onPlate(BELOW);
  const hasRoof = !vp.level && onPlate(ROOF);
  const outline = [...(hasBelow ? [plateOf(BELOW)] : []), ...shown, ...(hasRoof ? [plateOf(ROOF)] : [])].filter(Boolean);
  const plates = outline.map((p) => p.id);
  const { project, height } = makeStackProjector(b, Math.max(1, plates.length), { width });
  const rank = (id) => Math.max(1, plates.indexOf(id) + 1);
  return { width, height, plates, P: (x, y, level) => { const p = project(x, y, rank(level)); return [p[0], p[1] - 6]; }, project, rank, outline };
}

// One run on the canvas. Memoised: a drag, a pick elsewhere or a status on
// another run does not repaint it - only its own colour or pick does
// (guide: "only changed buckets are recoloured").
const Run = React.memo(function Run({ id, d, point, color, width, dash, on, label, onRun }) {
  return (
    <g className="g-run">
      {on && <path d={d} fill="none" stroke="var(--hi)" strokeWidth={width + 6} strokeLinecap="round" opacity="0.55" />}
      <path d={d} fill={point ? color : "none"} stroke={point ? "var(--ink)" : color} strokeWidth={point ? 0.8 : width + 1}
            strokeLinecap="round" strokeDasharray={dash} />
      <path d={d} fill="none" stroke="transparent" strokeWidth={COARSE ? 30 : 16} style={{ cursor: "pointer" }}
            onClick={(e) => { if (onRun(id, e.shiftKey)) e.stopPropagation(); }}>
        <title>{label}</title>
      </path>
    </g>
  );
});
const raf = (f) => (typeof window !== "undefined" && window.requestAnimationFrame ? window.requestAnimationFrame(f) : setTimeout(f, 16));

function SheetCanvas({ manifest, rows, allRows, vp, statusOf, picked, onPick, mode }) {
  const svgRef = useRef(null);
  const [drag, setDrag] = useState(null);
  const width = 1280;
  const proj = useMemo(() => makeProjection(manifest, vp, rows, width, allRows), [manifest, vp, rows, allRows]);
  const pickedSet = useMemo(() => new Set(picked || []), [picked]);

  // Every run's drawn points, in the SVG's own coordinates (hit testing).
  const drawn = useMemo(() => rows.map((r) => {
    const onPlate = (level) => (vp.projection === "plan" ? (x, y) => proj.P(x, y) : (x, y) => proj.P(x, y, level));
    let pts;
    if (r.level2 && vp.projection !== "plan" && proj.plates.includes(r.level2)) {
      const last = r.geom[r.geom.length - 1];
      pts = [onPlate(r.level)(r.geom[0][0], r.geom[0][1]), onPlate(r.level2)(last[0], last[1])];
    } else pts = r.geom.map(([x, y]) => onPlate(r.level)(x, y));
    const d = pts.length === 1
      ? `M${(pts[0][0] - 4).toFixed(1)} ${pts[0][1].toFixed(1)}a4 4 0 1 0 8 0a4 4 0 1 0 -8 0`
      : pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join("");
    return { id: r.id, r, pts, d };
  }), [rows, proj, vp.projection]);
  const index = useMemo(() => pointIndex(drawn), [drawn]);
  // A click picks through refs, so the handler stays the same object and
  // the memoised runs are not repainted when the pick changes elsewhere.
  const latest = useRef({});
  latest.current = { picked: picked || [], pickedSet, mode, onPick };
  const onRun = useCallback((id, shift) => {
    const { picked: pk, pickedSet: ps, mode: m, onPick: op } = latest.current;
    if (m !== "click") return false;
    op(shift ? (ps.has(id) ? pk.filter((x) => x !== id) : [...pk, id]) : [id]);
    return true;
  }, []);

  const toSvg = (e) => {
    const svg = svgRef.current;
    if (!svg || !svg.getScreenCTM) return [0, 0];
    const pt = svg.createSVGPoint(); pt.x = e.clientX; pt.y = e.clientY;
    const m = svg.getScreenCTM(); if (!m) return [0, 0];
    const q = pt.matrixTransform(m.inverse());
    return [q.x, q.y];
  };
  const down = (e) => {
    if (mode === "click" || e.button > 0) return;
    const p = toSvg(e);
    setDrag({ start: p, now: p, poly: [p], add: e.shiftKey });
    if (e.currentTarget.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId);
  };
  // Pointer moves are folded into one update an animation frame.
  const pending = useRef(null); const frame = useRef(0);
  const move = (e) => {
    if (!drag) return;
    pending.current = toSvg(e);
    if (frame.current) return;
    frame.current = raf(() => {
      frame.current = 0;
      const p = pending.current; pending.current = null;
      if (p) setDrag((d) => (d ? { ...d, now: p, poly: mode === "lasso" ? [...d.poly, p] : d.poly } : d));
    });
  };
  const up = () => {
    if (!drag) return;
    const now = pending.current || drag.now;
    const poly = pending.current ? [...drag.poly, pending.current] : drag.poly;
    pending.current = null;
    const shape = mode === "box"
      ? { kind: "box", x0: Math.min(drag.start[0], now[0]), x1: Math.max(drag.start[0], now[0]), y0: Math.min(drag.start[1], now[1]), y1: Math.max(drag.start[1], now[1]) }
      : { kind: "lasso", poly };
    const ids = pickByShape(drawn, shape, index);
    onPick(drag.add ? [...new Set([...(picked || []), ...ids])] : ids);
    setDrag(null);
  };

  // The plates, their rooms and labels: drawn once per projection, not on
  // every drag step or pick.
  const plates = useMemo(() => proj.outline.map((pl) => {
    const at = vp.projection === "plan" ? (p) => proj.P(p[0], p[1]) : (p) => proj.P(p[0], p[1], pl.id);
    const l = pl.lvl || {};
    const b = bounds([l]);
    const lp = vp.projection === "plan" ? proj.P(b.xmin, b.ymin + b.h) : proj.P(b.xmin, b.ymin, pl.id);
    return (
      <g key={pl.id}>
        {l.footprint && (
          <polygon points={pointsAttr(l.footprint, at)} fill={pl.roof ? "var(--roof)" : pl.dashed ? "none" : "var(--sheet)"}
                   stroke={pl.dashed ? "var(--ink2)" : "var(--ink)"} strokeWidth="1.2" strokeDasharray={pl.dashed ? "7 5" : undefined} />
        )}
        {!pl.dashed && (l.rooms || []).filter((rm) => rm.category !== "circ").map((rm) => (
          <polygon key={rm.id} points={pointsAttr(rm.polygon, at)} fill="var(--room)" stroke="var(--line)" strokeWidth="0.35" />
        ))}
        {(vp.projection !== "plan" || pl.dashed) && (
          <text x={lp[0]} y={lp[1] + (vp.projection === "plan" ? -8 : 22)} fontSize="16" fontWeight="600" fill="var(--ink)">
            {pl.label}{pl.note ? <tspan fontWeight="400" fill="var(--ink2)">{` · ${pl.note}`}</tspan> : null}
          </text>
        )}
      </g>
    );
  }), [proj, vp.projection]);

  return (
    <svg ref={svgRef} viewBox={`0 0 ${proj.width} ${proj.height}`} role="img" aria-label="Sheet viewport"
         style={{ touchAction: mode === "click" ? "auto" : "none", cursor: mode === "click" ? "default" : "crosshair" }}
         onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={() => setDrag(null)}>
      {plates}
      {drawn.map(({ id, r, pts, d }) => {
        const st = statusOf(r);
        return (
          <Run key={id} id={id} d={d} point={pts.length === 1} color={FIELD_COLOR[st] || FIELD_COLOR.projected} width={runWidth(r)} dash={runDash(r)}
               on={pickedSet.has(id)} label={`${r.id} ${r.type} - ${FIELD_LABEL[st] || st}`} onRun={onRun} />
        );
      })}
      {drag && mode === "box" && (
        <rect x={Math.min(drag.start[0], drag.now[0])} y={Math.min(drag.start[1], drag.now[1])} width={Math.abs(drag.now[0] - drag.start[0])} height={Math.abs(drag.now[1] - drag.start[1])}
              fill="rgba(31,111,209,.08)" stroke="#1F6FD1" strokeDasharray="4 3" style={{ pointerEvents: "none" }} />
      )}
      {drag && mode === "lasso" && drag.poly.length > 1 && (
        <polygon points={drag.poly.map((p) => p.map((v) => v.toFixed(1)).join(",")).join(" ")} fill="rgba(31,111,209,.08)" stroke="#1F6FD1" strokeDasharray="4 3" style={{ pointerEvents: "none" }} />
      )}
    </svg>
  );
}

const COL = {
  id: ["ID", (r) => r.id], level: ["Level", (r, m) => levelName(m, r.level)], type: ["Type", (r) => r.type], size: ["Size", (r) => r.size],
  qty: ["Qty", (r) => r.qty], lengthFt: ["Length (ft)", (r) => fmtFt(r.lengthFt)], from: ["From", (r) => r.from], to: ["To", (r) => r.to],
  status: ["Status", (r, m, st) => FIELD_LABEL[st] || st],
};

function StatusCounts({ rows, statusOf }) {
  const by = {};
  rows.forEach((r) => { const s = statusOf(r); by[s] = by[s] || { n: 0, ft: 0 }; by[s].n += r.qty || 1; by[s].ft += (r.lengthFt || 0) * (r.qty || 1); });
  return (
    <div className="g-sheet-block">
      <b>Status</b>
      {Object.keys(FIELD_LABEL).map((s) => (
        <div key={s} className="g-sheet-legend"><i style={{ background: FIELD_COLOR[s] }} />{FIELD_LABEL[s]}<span>{(by[s] && by[s].n) || 0} · {fmtFt((by[s] && by[s].ft) || 0)} ft</span></div>
      ))}
    </div>
  );
}

export function SheetPage({ manifest, view, page, statusOf, picked, onPick, mode, today, ready = false }) {
  const viewports = page.viewports || [];
  const pageRows = useMemo(() => {
    if (!viewports.length) return view.rows || [];
    const ids = new Set();
    viewports.forEach((vp) => viewportRows(view.rows, vp).forEach((r) => ids.add(r.id)));
    return (view.rows || []).filter((r) => ids.has(r.id));
  }, [view.rows, viewports]);
  const [w, h] = SIZES[page.size] || SIZES["ARCH D"];
  const schedule = (page.blocks || []).find((b) => b.type === "schedule");
  const cols = ((schedule && schedule.columns) || ["id", "type", "lengthFt", "status"]).filter((c) => COL[c]);
  const pickedSet = new Set(picked || []);
  return (
    <div className="g-sheet" style={schedule ? undefined : { aspectRatio: `${w} / ${h}` }} data-size={page.size || "ARCH D"} data-render-ready={ready ? "1" : undefined}>
      {viewports.length > 0 && (
        <div className="g-sheet-body">
          <div className="g-sheet-draw">
            {viewports.map((vp, i) => (
              <SheetCanvas key={i} manifest={manifest} vp={vp} rows={viewportRows(view.rows, vp)} allRows={view.rows} statusOf={statusOf} picked={picked} onPick={onPick} mode={mode} />
            ))}
          </div>
          <div className="g-sheet-side">
            {(page.blocks || []).some((b) => b.type === "legend" || b.type === "status") && <StatusCounts rows={pageRows} statusOf={statusOf} />}
            {(page.blocks || []).filter((b) => b.type === "notes" && b.text).map((b, i) => <div key={i} className="g-sheet-block">{b.text}</div>)}
          </div>
        </div>
      )}
      {!viewports.length && (page.blocks || []).some((b) => b.type === "legend" || b.type === "status") && (
        <div className="g-sheet-strip"><StatusCounts rows={pageRows} statusOf={statusOf} /></div>
      )}
      {schedule && (
        <div className="g-sheet-block g-sheet-schedule">
          <table>
            <thead><tr>{cols.map((c) => <th key={c}>{COL[c][0]}</th>)}</tr></thead>
            <tbody>
              {pageRows.map((r) => (
                <tr key={r.id} className={pickedSet.has(r.id) ? "is-hi" : undefined} onClick={(e) => onPick(e.shiftKey ? [...pickedSet, r.id] : [r.id])}>
                  {cols.map((c) => <td key={c}>{COL[c][1](r, manifest, statusOf(r))}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(page.blocks || []).some((b) => b.type === "title") && (
        <div className="g-sheet-title">
          <div><b>{manifest.title || "Project"}</b><span>{page.title}</span></div>
          <div><span>{view.title}, version {view.version}</span><span>Status as of {today}</span></div>
          <div><span>{page.size || "ARCH D"}</span><span>{pageRows.length} runs · BidLight</span></div>
        </div>
      )}
    </div>
  );
}

export default function SheetPages({ projectId, manifest, view, statusByRow, picked, onPick, canEdit = false, printPageId = null, reloadKey = 0 }) {
  const [all, setAll] = useState(null);
  const [pageId, setPageId] = useState(null);
  const [mode, setMode] = useState("click");
  const [showHidden, setShowHidden] = useState(false);
  const [editing, setEditing] = useState(null);     // the page being made or changed
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // The statuses as the tracking data says: what the page uses when the
  // tracking panel is not there to hand them in (the printed sheet).
  const [ownStates, setOwnStates] = useState(null);
  const [printing, setPrinting] = useState("");
  // Guide 19.3: any page replayed to a past date ("" = now).
  const [asOf, setAsOf] = useState("");
  const [notReplayed, setNotReplayed] = useState(0);
  const load = useCallback(() => viewsApi(projectId, `/${view.viewId}/tracking${asOf ? `?asOf=${asOf}` : ""}`).then((t) => {
    setAll(t.pages || []);
    setOwnStates(new Map((t.elements || []).map((e) => [e.id, e.status || "projected"])));
    setNotReplayed((t.elements || []).filter((e) => e.notReplayed).length);
  }).catch(() => setAll([])), [projectId, view.viewId, asOf]);
  useEffect(() => { setAll(null); load(); }, [load, view.version]);
  useEffect(() => { if (reloadKey) load(); }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!all || (!all.length && !canEdit)) return null;
  const pages = all.filter((p) => showHidden || !p.hidden);
  const hiddenN = all.filter((p) => p.hidden).length;
  const page = printPageId ? all.find((p) => p.pageId === printPageId) || null : pages.find((p) => p.pageId === pageId) || pages[0] || null;
  // A replayed page reads its own states, never the live ones the tracking table hands in.
  const states = asOf ? ownStates : (statusByRow || ownStates);
  const statusOf = (r) => (states && states.get(r.id)) || "projected";
  const today = asOf || new Date().toISOString().slice(0, 10);
  const act = async (fn, after) => {
    setBusy(true); setErr("");
    try { const out = await fn(); await load(); if (after) after(out); } catch (e) { setErr(e.message || "Could not save the page"); } finally { setBusy(false); }
  };
  const save = (draft) => act(
    () => (draft.pageId && draft.kind === "custom"
      ? viewsApi(projectId, `/${view.viewId}/pages/${encodeURIComponent(draft.pageId)}`, { method: "PATCH", body: draft })
      : viewsApi(projectId, `/${view.viewId}/pages`, { method: "POST", body: { ...draft, pageId: undefined, kind: undefined } })),
    (out) => { setEditing(null); if (out && out.pageId) setPageId(out.pageId); },
  );
  const move = (dir) => {
    const i = all.findIndex((p) => p.pageId === page.pageId); const j = i + dir;
    if (j < 0 || j >= all.length) return;
    const a = all[i]; const b = all[j];
    act(async () => {
      await viewsApi(projectId, `/${view.viewId}/pages/${encodeURIComponent(a.pageId)}`, { method: "PATCH", body: { order: b.order } });
      await viewsApi(projectId, `/${view.viewId}/pages/${encodeURIComponent(b.pageId)}`, { method: "PATCH", body: { order: a.order } });
    });
  };
  // A page's name in the switcher; a long one (a task's page) is cut, and says it all on hover.
  const label = (p) => {
    const full = `${p.title.replace(`${view.title} — `, "")}${p.hidden ? " (hidden)" : ""}`;
    return full.length > 34 ? <span title={full}>{`${full.slice(0, 32)}…`}</span> : full;
  };
  if (printPageId) {
    return page ? <section className="g-sheets g-sheet-print"><SheetPage manifest={manifest} view={view} page={page} statusOf={statusOf} picked={[]} onPick={() => {}} mode="click" today={today} ready /></section>
      : <div className="g-empty">This tracking sheet no longer exists.</div>;
  }
  const pdfPath = page ? `/projects/${projectId}/documentation/views/${encodeURIComponent(view.viewId)}/sheets/${encodeURIComponent(page.pageId)}/pdf` : "";
  const printPdf = async () => {
    setPrinting("pdf"); setErr("");
    try { await downloadAuthed(pdfPath, `${page.title} - tracking sheet.pdf`); } catch (e) { setErr(e.message || "Could not print the sheet"); } finally { setPrinting(""); }
  };
  const fileIt = async () => {
    setPrinting("save"); setErr("");
    try {
      const r = await fetch(`${url}${pdfPath}/save`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem(tokenRef)}` }, body: "{}" });
      const out = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(out.err || `HTTP ${r.status}`);
      message.success(out.duplicate ? "Already in Document Control, unchanged" : `Filed in Document Control${out.doc && out.doc.revisionNumber != null ? `, revision ${out.doc.revisionNumber}` : ""}`);
    } catch (e) { setErr(e.message || "Could not file the sheet"); } finally { setPrinting(""); }
  };
  return (
    <section className="g-sheets">
      <h2>Tracking sheets</h2>
      <p className="g-note">Pages drawn from this view, coloured by field status. Click a run, or drag a box or a lasso, to pick runs; hold Shift to add. The pick is the same one the tracking table below uses.</p>
      <div className="g-ctrl">
        {pages.length > 0 && <div className="g-segscroll"><Segmented size="small" value={page && page.pageId} onChange={setPageId} options={pages.map((p) => ({ label: label(p), value: p.pageId }))} /></div>}
        <Segmented size="small" value={mode} onChange={setMode} options={[{ label: "Click", value: "click" }, { label: "Box", value: "box" }, { label: "Lasso", value: "lasso" }]} />
        {(picked || []).length > 0 && <Tooltip title="Clear the pick"><button type="button" className="g-linkbtn" onClick={() => onPick([])}>{picked.length} picked · clear</button></Tooltip>}
        <label className="g-tsub g-noprint" htmlFor={`asof-${view.viewId}`}>Status as of{" "}
          <input id={`asof-${view.viewId}`} type="date" value={asOf} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setAsOf(e.target.value)} />
        </label>
        {asOf && <button type="button" className="g-linkbtn g-noprint" onClick={() => setAsOf("")}>Back to today</button>}
        {asOf && notReplayed > 0 && <span className="g-tsub">{notReplayed} row{notReplayed === 1 ? "" : "s"} linked to the model show today's field status</span>}
        {page && <Tooltip title={`This page as a PDF at ${page.size || "ARCH D"}, stamped with the view version and today's status`}><button type="button" className="g-linkbtn g-noprint" disabled={!!printing} onClick={printPdf}>{printing === "pdf" ? "Printing…" : "Print PDF"}</button></Tooltip>}
        {page && canEdit && <button type="button" className="g-linkbtn g-noprint" disabled={!!printing} onClick={fileIt}>{printing === "save" ? "Filing…" : "Save to Doc Control"}</button>}
      </div>
      {canEdit && (
        <div className="g-ctrl g-noprint">
          <button type="button" className="g-linkbtn" onClick={() => setEditing(blankPage(page && page.viewports && page.viewports[0] ? page.viewports[0].level : ""))}>New page</button>
          {page && page.kind === "custom" && <button type="button" className="g-linkbtn" onClick={() => setEditing(page)}>Edit page</button>}
          {page && page.kind !== "custom" && <button type="button" className="g-linkbtn" onClick={() => setEditing({ ...page, pageId: undefined, kind: undefined, title: `${page.title} (copy)` })}>Copy to a new page</button>}
          {page && <button type="button" className="g-linkbtn" disabled={busy} onClick={() => act(() => viewsApi(projectId, `/${view.viewId}/pages/${encodeURIComponent(page.pageId)}`, { method: "PATCH", body: { hidden: !page.hidden } }))}>{page.hidden ? "Show page" : "Hide page"}</button>}
          {page && <button type="button" className="g-linkbtn" disabled={busy} onClick={() => move(-1)} aria-label="Move page left">Move left</button>}
          {page && <button type="button" className="g-linkbtn" disabled={busy} onClick={() => move(1)} aria-label="Move page right">Move right</button>}
          {page && page.kind === "custom" && view.canDelete && <button type="button" className="g-linkbtn" disabled={busy} onClick={() => act(() => viewsApi(projectId, `/${view.viewId}/pages/${encodeURIComponent(page.pageId)}`, { method: "DELETE" }), () => setPageId(null))}>Delete page</button>}
          {hiddenN > 0 && <button type="button" className="g-linkbtn" onClick={() => setShowHidden(!showHidden)}>{showHidden ? "Hide hidden pages" : `Show hidden pages (${hiddenN})`}</button>}
          {err && <span className="g-err">{err}</span>}
        </div>
      )}
      {page ? <SheetPage manifest={manifest} view={view} page={page} statusOf={statusOf} picked={picked} onPick={onPick} mode={mode} today={today} />
        : <div className="g-empty">No pages yet. Publish the view for its default pages, or make one with New page.</div>}
      {editing && <PageEditor open manifest={manifest} view={view} initial={editing} busy={busy} onCancel={() => setEditing(null)} onSave={save} />}
    </section>
  );
}
